import {
  PROTOCOL_VERSION,
  roomDirectoryResponseSchema,
  roomAdmissionResponseSchema,
  type RoomDirectoryEntry,
  type RoomAdmissionResponse,
  type RoomVisibility,
} from '@2d-space/contracts';

import { readRoomAuthority, writeRoomAuthority } from './room-authority-storage.js';

export async function listRooms(
  serverUrl: string,
  visibility: RoomVisibility,
  search: string,
): Promise<readonly RoomDirectoryEntry[]> {
  const url = new URL('/rooms', serverUrl);
  url.searchParams.set('visibility', visibility);
  if (search.trim() !== '') url.searchParams.set('q', search.trim());
  const response = await fetch(url);
  const parsed = roomDirectoryResponseSchema.safeParse(await response.json());
  if (!parsed.success) throw new Error('The room directory returned an invalid response.');
  return parsed.data.rooms;
}

export async function enterPublicRoom(serverUrl: string, slug: string): Promise<string> {
  const authority = readRoomAuthority(globalThis.localStorage, serverUrl, slug);
  const result = await postAdmission(
    new URL(`/rooms/${encodeURIComponent(slug)}/admission`, serverUrl),
    {
      version: PROTOCOL_VERSION,
      ...authority,
    },
  );
  return resolveAdmission(serverUrl, slug, result);
}

export async function enterPrivateRoom(
  serverUrl: string,
  slug: string,
  code: string,
): Promise<string> {
  const authority = readRoomAuthority(globalThis.localStorage, serverUrl, slug);
  const result = await postAdmission(new URL('/rooms/private-code', serverUrl), {
    version: PROTOCOL_VERSION,
    slug,
    code: code.trim().toUpperCase(),
    ...authority,
  });
  const path = await resolveAdmission(serverUrl, slug, result);
  const current = readRoomAuthority(globalThis.localStorage, serverUrl, slug);
  if (current !== undefined) {
    writeRoomAuthority(globalThis.localStorage, serverUrl, slug, {
      ...current,
      privateAccessCode: code.trim().toUpperCase(),
    });
  }
  return path;
}

async function postAdmission(url: URL, body: object): Promise<RoomAdmissionResponse> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const parsed = roomAdmissionResponseSchema.safeParse(await response.json());
  if (!parsed.success) throw new Error('The room server returned an invalid response.');
  return parsed.data;
}

async function resolveAdmission(
  serverUrl: string,
  slug: string,
  result: RoomAdmissionResponse,
): Promise<string> {
  if (result.ok) return admissionPath(serverUrl, result);
  if (result.error.code !== 'approval-required' || result.error.browserCredential === undefined) {
    throw new Error(admissionMessage(result.error.code));
  }
  const browserCredential = result.error.browserCredential;
  const current = readRoomAuthority(globalThis.localStorage, serverUrl, slug);
  writeRoomAuthority(globalThis.localStorage, serverUrl, slug, {
    ...current,
    browserCredential,
  });
  const createdResponse = await fetch(new URL('/rooms/join-requests', serverUrl), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ version: PROTOCOL_VERSION, slug, browserCredential }),
  });
  const created = (await createdResponse.json()) as unknown;
  if (
    typeof created !== 'object' ||
    created === null ||
    !('requestSecret' in created) ||
    typeof created.requestSecret !== 'string'
  ) {
    throw new Error('The join request could not be created.');
  }
  const requestSecret = created.requestSecret;
  for (;;) {
    await new Promise((resolve) => globalThis.setTimeout(resolve, 2_000));
    const pollResponse = await fetch(new URL('/rooms/join-requests/poll', serverUrl), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ version: PROTOCOL_VERSION, requestSecret }),
    });
    const poll = (await pollResponse.json()) as unknown;
    if (typeof poll !== 'object' || poll === null || !('status' in poll))
      throw new Error('The join request could not be checked.');
    if (poll.status === 'rejected')
      throw new Error('The room owner or admin declined this request.');
    if (poll.status === 'expired')
      throw new Error('Nobody approved the request in time. Try again.');
    if (poll.status === 'approved' && 'token' in poll && typeof poll.token === 'string') {
      return `/r/${encodeURIComponent(slug)}?t=${encodeURIComponent(poll.token)}`;
    }
  }
}

function admissionPath(
  serverUrl: string,
  result: Extract<RoomAdmissionResponse, { ok: true }>,
): string {
  const current = readRoomAuthority(globalThis.localStorage, serverUrl, result.slug);
  writeRoomAuthority(globalThis.localStorage, serverUrl, result.slug, {
    ...current,
    browserCredential: result.browserCredential,
  });
  return `/r/${encodeURIComponent(result.slug)}?t=${encodeURIComponent(result.token)}`;
}

function admissionMessage(
  code: Exclude<RoomAdmissionResponse, { ok: true }>['error']['code'],
): string {
  switch (code) {
    case 'invalid-code':
      return 'That private room code is not valid.';
    case 'room-full':
      return 'This room is full.';
    case 'room-locked':
      return 'This room is locked.';
    case 'approval-required':
      return 'An owner or admin must approve this browser.';
    case 'browser-blocked':
      return 'This browser is blocked from this room.';
    case 'rate-limited':
      return 'Too many attempts. Wait a moment and try again.';
    case 'room-closed':
      return 'This room has been closed.';
    case 'unknown-room':
      return 'This room no longer exists.';
    case 'wrong-visibility':
    case 'invalid-request':
    case 'internal-error':
      return 'The room could not be opened.';
  }
}
